import React, { useState, useMemo, useCallback } from 'react';
import { useAppContext } from '../contexts/AppContext';
import ActiveProjectBadge from '../components/ActiveProjectBadge';
import { normalizeProjectName, extractProjectName } from '../utils/validation';
import {
    TestTube2, AlertCircle, TrendingUp, Clock, Scale, BookOpen,
    FileBarChart, Settings, Activity,
    Calendar, CheckCircle2, XCircle, AlertTriangle, FileText,
    Search, Save, ChevronRight, PieChart,
    Beaker, Microscope, Thermometer, ShieldCheck, Link2, ExternalLink,
    Plus, BarChart3
} from 'lucide-react';
import { ModuleInfoModal } from '../components/ModuleInfoModal';

type TabType = 'specs' | 'traceability' | 'results' | 'dashboard' | 'entry' | 'analytics';

const ReliabilityTestPlan: React.FC = () => {
    const { allData, activeProject, exportProject } = useAppContext();
    const [activeTab, setActiveTab] = useState<TabType>('dashboard');
    const [selectedTestId, setSelectedTestId] = useState<string | null>(null);

    // Get iterations dynamically from iterationHistory
    const projectIterations = useMemo(() => {
        if (!allData.iterationHistory || allData.iterationHistory.length === 0) return [];

        // Filter by active project if needed
        const filtered = activeProject === 'all'
            ? allData.iterationHistory
            : allData.iterationHistory.filter(iter => {
                const iterProjectName = extractProjectName(iter.epochName || '');
                return normalizeProjectName(iterProjectName) === normalizeProjectName(activeProject);
            });

        return filtered.sort((a, b) => (b.epochNumber || b.iteration || 0) - (a.epochNumber || a.iteration || 0));
    }, [allData.iterationHistory, activeProject]);

    // Get latest iteration number
    const latestIteration = projectIterations.length > 0
        ? (projectIterations[0].epochNumber || projectIterations[0].iteration || 1)
        : 1;

    const [selectedIteration, setSelectedIteration] = useState<number>(latestIteration);



    // Project filtering helper
    const isItemInProject = useCallback((projName?: string, epochId?: number) => {
        if (activeProject === 'all') return true;
        const activeProjectNormalized = normalizeProjectName(activeProject);

        if (projName) {
            return normalizeProjectName(projName) === activeProjectNormalized;
        }

        // Project Inference from Epoch
        if (epochId) {
            const epoch = allData.iterationHistory?.find(e =>
                e.epochNumber === epochId || e.iteration === epochId
            );
            if (epoch) {
                const derivedName = extractProjectName(epoch.epochName || '');
                return normalizeProjectName(derivedName) === activeProjectNormalized;
            }
        }

        // Match project names from epoch titles if possible (Legacy check)
        if (activeProject.includes(projName || '') || (projName && projName.includes(activeProject))) return true;

        return false;
    }, [activeProject, allData.iterationHistory]);

    // Derive test results from global context, filtered by active project
    const localTestResults = useMemo(() => {
        const results = (allData.testResults || []) as any[];
        return results.filter(r => isItemInProject(r.projectName));
    }, [allData.testResults, isItemInProject]);

    // Dynamic project tests from allData.dvprTests (replaces hardcoded testSpecifications seed)
    const projectTests = useMemo(() => {
        const tests = (allData.dvprTests || []).filter(test =>
            isItemInProject(test.projectName, test.epochId)
        );

        // Map to TestSpecification format expected by UI components
        return tests.map(test => ({
            testId: test.testId,
            testName: test.testName,
            testType: 'Reliability' as const,  // DvprTest doesn't have testType field
            testStandard: 'Custom',  // DvprTest doesn't have testStandard field  
            linkedFmeaIds: test.traceableRiskId || [],
            linkedBomIds: [] as string[],  // DvprTest doesn't have bomIds field
            objectiveStatement: test.procedure || 'No procedure defined',
            testMethod: test.procedure || '',  // Use procedure as testMethod
            sampleSize: 1,  // DvprTest doesn't have sampleSize field
            testDuration: 'As specified',  // DvprTest doesn't have duration field
            testEquipment: [] as string[],  // DvprTest doesn't have equipment field
            testEnvironment: {} as any  // DvprTest doesn't have environment field
        }));
    }, [allData.dvprTests, isItemInProject]);

    // --- UTILITY FUNCTIONS ---

    const getTestIcon = (type: string) => {
        switch (type) {
            case 'Reliability': return <Activity size={18} className="text-blue-500" />;
            case 'Performance': return <TrendingUp size={18} className="text-purple-500" />;
            case 'Safety': return <ShieldCheck size={18} className="text-red-500" />;
            case 'Environmental': return <Thermometer size={18} className="text-orange-500" />;
            case 'Material': return <Microscope size={18} className="text-emerald-500" />;
            case 'Regulatory': return <Scale size={18} className="text-slate-500" />;
            default: return <TestTube2 size={18} className="text-slate-500" />;
        }
    };

    // --- COMPONENT: TEST SPECIFICATION VIEWER ---

    const TestSpecificationViewer = () => {
        const selectedSpec = selectedTestId ? projectTests.find(t => t.testId === selectedTestId) : projectTests[0];
        const params: any[] = [];  // testParameters are not available in DvprTest
        const criteria: any[] = [];  // passFailCriteria are not available in DvprTest

        return (
            <div className="flex h-[calc(100vh-240px)] gap-6">
                {/* List Sidebar */}
                <div className="w-1/3 bg-card border border-border rounded-xl flex flex-col overflow-hidden">
                    <div className="p-4 border-b border-border bg-slate-50 dark:bg-slate-800/50">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={16} />
                            <input
                                type="text"
                                placeholder="Search specifications..."
                                className="w-full pl-9 pr-4 py-2 bg-white dark:bg-black/20 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                            />
                        </div>
                    </div>
                    <div className="overflow-y-auto flex-1 p-2">
                        {projectTests.map(spec => (
                            <div
                                key={spec.testId}
                                onClick={() => setSelectedTestId(spec.testId)}
                                className={`p-3 rounded-lg mb-1 cursor-pointer transition-all ${(selectedTestId || projectTests[0]?.testId) === spec.testId
                                    ? 'bg-primary/10 border-primary/20 shadow-sm'
                                    : 'hover:bg-slate-50 dark:hover:bg-slate-800/50 border border-transparent'
                                    }`}
                            >
                                <div className="flex items-start justify-between">
                                    <h4 className={`font-semibold text-sm ${(selectedTestId || projectTests[0]?.testId) === spec.testId ? 'text-primary' : 'text-text-primary'
                                        }`}>{spec.testName}</h4>
                                    <span className="text-xs font-mono text-text-secondary bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                                        {spec.testId}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                    <span className="flex items-center gap-1 text-xs text-text-secondary bg-white dark:bg-black/20 border border-border px-1.5 py-0.5 rounded-full">
                                        {getTestIcon(spec.testType)} {spec.testType}
                                    </span>
                                    <span className="text-xs text-text-secondary truncate max-w-[120px]" title={spec.testStandard}>
                                        {spec.testStandard}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Detail View */}
                <div className="flex-1 bg-card border border-border rounded-xl overflow-y-auto p-6 shadow-sm">
                    {selectedSpec && (
                        <div>
                            <div className="flex justify-between items-start mb-6 border-b border-border pb-6">
                                <div>
                                    <h2 className="text-2xl font-bold text-text-primary flex items-center gap-3">
                                        {selectedSpec.testName}
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${selectedSpec.testType === 'Reliability' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                            selectedSpec.testType === 'Safety' ? 'bg-red-50 text-red-700 border-red-200' :
                                                'bg-slate-50 text-slate-700 border-slate-200'
                                            }`}>
                                            {selectedSpec.testType}
                                        </span>
                                    </h2>
                                    <p className="text-text-secondary mt-2 text-lg">{selectedSpec.objectiveStatement}</p>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm text-text-secondary mb-1">Standard</div>
                                    <div className="font-mono font-medium text-text-primary bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded">
                                        {selectedSpec.testStandard}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-6 mb-8">
                                <div className="bg-slate-50 dark:bg-slate-800/30 p-4 rounded-lg border border-border">
                                    <h4 className="text-sm font-semibold uppercase text-text-secondary mb-2 flex items-center gap-2">
                                        <Beaker size={16} /> Test Method
                                    </h4>
                                    <p className="text-sm text-text-primary leading-relaxed">{selectedSpec.testMethod}</p>
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-800/30 p-4 rounded-lg border border-border">
                                    <h4 className="text-sm font-semibold uppercase text-text-secondary mb-2 flex items-center gap-2">
                                        <Scale size={16} /> Equipment
                                    </h4>
                                    <ul className="text-sm text-text-primary list-disc list-inside space-y-1">
                                        {selectedSpec.testEquipment.map((eq, i) => <li key={i}>{eq}</li>)}
                                    </ul>
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-800/30 p-4 rounded-lg border border-border">
                                    <h4 className="text-sm font-semibold uppercase text-text-secondary mb-2 flex items-center gap-2">
                                        <Clock size={16} /> Conditions
                                    </h4>
                                    <div className="space-y-2 text-sm text-text-primary">
                                        <div className="flex justify-between border-b border-border/50 pb-1">
                                            <span>Duration:</span>
                                            <span className="font-mono">{selectedSpec.testDuration}</span>
                                        </div>
                                        <div className="flex justify-between border-b border-border/50 pb-1">
                                            <span>Sample Size:</span>
                                            <span className="font-mono">{selectedSpec.sampleSize}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Env:</span>
                                            <span className="font-mono">{selectedSpec.testEnvironment.temperature || 'Ambient'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-lg font-bold text-text-primary mb-4 flex items-center gap-2">
                                        <Settings size={20} className="text-primary" />
                                        Test Parameters
                                    </h3>
                                    <div className="bg-white dark:bg-black/20 border border-border rounded-lg overflow-hidden">
                                        <table className="w-full text-sm">
                                            <thead className="bg-slate-50 dark:bg-slate-800 border-b border-border text-left">
                                                <tr>
                                                    <th className="p-3 font-semibold text-text-secondary">Parameter</th>
                                                    <th className="p-3 font-semibold text-text-secondary">Nominal</th>
                                                    <th className="p-3 font-semibold text-text-secondary">Limits</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-border">
                                                {params.map(p => (
                                                    <tr key={p.parameterId} className="hover:bg-slate-50 dark:hover:bg-white/5">
                                                        <td className="p-3 font-medium">{p.parameterName}</td>
                                                        <td className="p-3 font-mono text-text-secondary">{p.nominalValue} {p.unit}</td>
                                                        <td className="p-3 font-mono text-xs text-text-secondary bg-slate-50 dark:bg-white/5 rounded">
                                                            {p.lowerLimit} - {p.upperLimit} {p.unit}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-lg font-bold text-text-primary mb-4 flex items-center gap-2">
                                        <CheckCircle2 size={20} className="text-green-600" />
                                        Pass/Fail Criteria
                                    </h3>
                                    <div className="space-y-3">
                                        {criteria.map((c) => (
                                            <div key={c.criteriaId} className="p-3 rounded-lg border border-border bg-white dark:bg-black/20">
                                                <div className="font-semibold text-sm mb-1">{c.description}</div>
                                                <div className="text-xs text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 p-2 rounded mb-1">
                                                    <span className="font-bold mr-1">PASS:</span> {c.acceptanceCriteria}
                                                </div>
                                                <div className="text-xs text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                                                    <span className="font-bold mr-1">FAIL:</span> {c.rejectCriteria}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // --- COMPONENT: TRACEABILITY MATRIX ---

    const TraceabilityMatrix = () => {
        const filteredFmeaItems = useMemo(() =>
            (allData?.fmeaItems || []).filter(item => isItemInProject(item.projectName)),
            [allData.fmeaItems, isItemInProject]);

        // Merge/Select tests to display
        const displayedTests = useMemo(() => {
            // 1. Get tests from active project
            const projectTests = (allData.dvprTests || []).filter(t => isItemInProject(t.projectName, t.epochId));

            // 2. If we have project tests, use them. 
            // If not, and we are in 'Aerator Pro' (or no project), use seed data.
            // Using a heuristic: if projectTests > 0, we prefer them.
            if (projectTests.length > 0) {
                // Map DvprTest to the shape expected by the UI (TestSpecification-like)
                return projectTests.map(t => ({
                    testId: t.testId,
                    testName: t.testName,
                    testStandard: 'Custom / Imported', // Fallback
                    linkedFmeaIds: t.traceableRiskId || [],
                    // ... other fields might be simulated or mapped if DvprTest is expanded
                    testType: 'Reliability', // Default
                    objectiveStatement: t.procedure || 'No procedure defined'
                }));
            }

            // No fallback - return empty array if no tests found
            return [];
        }, [allData.dvprTests, activeProject, isItemInProject]);

        return (
            <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden flex flex-col h-[calc(100vh-200px)]">
                <div className="p-6 border-b border-border">
                    <h2 className="text-xl font-bold text-text-primary flex items-center gap-2">
                        <Link2 className="text-primary" /> DVP&R Traceability Matrix
                    </h2>
                    <p className="text-text-secondary text-sm">Validating risk mitigation through linked tests for {activeProject || 'All Projects'}</p>
                </div>
                <div className="overflow-auto flex-1 p-0">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs text-text-secondary uppercase bg-slate-50 dark:bg-slate-800 border-b border-border sticky top-0 z-10">
                            <tr>
                                <th className="px-6 py-3 font-semibold">Test ID</th>
                                <th className="px-6 py-3 font-semibold">Test Name</th>
                                <th className="px-6 py-3 font-semibold">Linked Failure Mode (FMEA)</th>
                                <th className="px-6 py-3 font-semibold">Criteria</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {displayedTests.length > 0 ? (
                                displayedTests.map(spec => {
                                    // Find linked FMEA items
                                    const linkedRisks = filteredFmeaItems.filter((f: any) => spec.linkedFmeaIds.includes(f.id));

                                    return (
                                        <tr key={spec.testId} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                                            <td className="px-6 py-4 font-mono text-text-secondary font-medium">{spec.testId}</td>
                                            <td className="px-6 py-4">
                                                <div className="font-bold text-text-primary">{spec.testName}</div>
                                                <div className="text-xs text-text-secondary">{spec.testStandard}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {linkedRisks.length > 0 ? (
                                                    <div className="space-y-2">
                                                        {linkedRisks.map((risk: any) => (
                                                            <div key={risk.id} className="flex items-start gap-2 p-2 bg-slate-100 dark:bg-slate-800 rounded border border-border/50">
                                                                <AlertCircle size={14} className="text-orange-500 mt-0.5 shrink-0" />
                                                                <div>
                                                                    <div className="text-xs font-bold text-text-primary mb-0.5">{risk.id}</div>
                                                                    <div className="text-xs text-text-secondary line-clamp-2">{risk.failureMode}</div>
                                                                </div>
                                                                <a href={`/fmeas?id=${risk.id}`} target="_blank" rel="noreferrer" className="ml-auto text-primary hover:text-primary/80">
                                                                    <ExternalLink size={12} />
                                                                </a>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="text-text-secondary italic">No linked risks</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 max-w-xs">
                                                <span className="text-xs text-text-secondary">{spec.objectiveStatement}</span>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={5} className="p-8 text-center text-text-secondary italic">
                                        No tests found for this project. Use the &quot;Lab Entry&quot; tab or import DVP&amp;R data.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // --- COMPONENT: ITERATION DASHBOARD ---

    const IterationDashboard = () => {
        const results = localTestResults.filter(r => r.iterationNumber === selectedIteration);
        const passed = results.filter(r => r.overallResult === 'Pass').length;
        const failed = results.filter(r => r.overallResult === 'Fail').length;
        const passRate = results.length > 0 ? Math.round((passed / results.length) * 100) : 0;

        return (
            <div className="space-y-6">
                {/* Dashboard Header & Stats */}
                <div className="grid grid-cols-4 gap-4">
                    <div className="bg-card border border-border rounded-xl p-5 shadow-sm flex flex-col justify-between">
                        <div>
                            <div className="text-text-secondary text-sm font-medium mb-1">Selected Iteration</div>
                            <div className="flex items-center gap-3">
                                <select
                                    value={selectedIteration}
                                    onChange={(e) => setSelectedIteration(Number(e.target.value))}
                                    className="text-2xl font-bold bg-transparent border-none focus:outline-none cursor-pointer hover:text-primary transition-colors"
                                >
                                    {projectIterations.length > 0 ? (
                                        projectIterations.map(iter => {
                                            const iterNum = iter.epochNumber || iter.iteration || 0;
                                            return (
                                                <option key={iterNum} value={iterNum}>
                                                    {iter.epochName || `Iteration ${iterNum}`}
                                                </option>
                                            );
                                        })
                                    ) : (
                                        <option value={1}>Iteration 1</option>
                                    )}
                                </select>
                                <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wide ${projectIterations.find(i => (i.epochNumber || i.iteration) === selectedIteration)?.phase === 'Production'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-slate-100 text-slate-800'
                                    }`}>
                                    {projectIterations.find(i => (i.epochNumber || i.iteration) === selectedIteration)?.phase?.toUpperCase() || 'DRAFT'}
                                </span>
                            </div>
                        </div>
                        <div className="text-xs text-text-secondary mt-2">
                            {results.length > 0 ? results[0].iterationDate : 'No Date'}
                        </div>
                    </div>

                    <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
                        <div className="text-text-secondary text-sm font-medium mb-1">Pass Rate</div>
                        <div className="flex items-end gap-2">
                            <h3 className={`text-3xl font-bold ${passRate >= 90 ? 'text-green-600' : passRate >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                                {passRate}%
                            </h3>
                            <span className="text-sm text-text-secondary mb-1">of {results.length} tests</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2 mt-3 overflow-hidden">
                            <div className={`h-full rounded-full transition-all duration-500 ${passRate >= 90 ? 'bg-green-500' : passRate >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                                }`} style={{ width: `${passRate}%` }}></div>
                        </div>
                    </div>

                    <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
                        <div className="text-text-secondary text-sm font-medium mb-1">Test Performance</div>
                        <div className="flex justify-between mt-2">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-green-600">{passed}</div>
                                <div className="text-xs text-text-secondary uppercase mt-1">Pass</div>
                            </div>
                            <div className="w-px bg-border mx-2"></div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-red-600">{failed}</div>
                                <div className="text-xs text-text-secondary uppercase mt-1">Fail</div>
                            </div>
                            <div className="w-px bg-border mx-2"></div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-slate-600">{results.length}</div>
                                <div className="text-xs text-text-secondary uppercase mt-1">Total</div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-card border border-border rounded-xl p-5 shadow-sm flex flex-col justify-center items-center text-center cursor-pointer hover:border-primary/50 transition-colors border-dashed border-2">
                        <div className="p-3 bg-primary/10 rounded-full text-primary mb-2">
                            <FileBarChart size={24} />
                        </div>
                        <span className="text-sm font-medium text-primary">Generate Iteration Report</span>
                    </div>
                </div>

                {/* Results Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {/* Failed Items First */}
                    {results.sort((a) => (a.overallResult === 'Fail' ? -1 : 1)).map(result => {
                        const spec = projectTests.find(s => s.testId === result.testId);
                        return (
                            <div key={result.resultId} className={`bg-card border rounded-xl overflow-hidden shadow-sm transition-all hover:shadow-md ${result.overallResult === 'Fail' ? 'border-red-200 dark:border-red-900' : 'border-border'
                                }`}>
                                <div className={`p-3 border-b flex justify-between items-center ${result.overallResult === 'Fail'
                                    ? 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800'
                                    : 'bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800'
                                    }`}>
                                    <div className="flex items-center gap-2">
                                        {result.overallResult === 'Fail'
                                            ? <XCircle size={18} className="text-red-600" />
                                            : <CheckCircle2 size={18} className="text-green-600" />
                                        }
                                        <span className={`font-bold text-sm ${result.overallResult === 'Fail' ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'
                                            }`}>{result.overallResult.toUpperCase()}</span>
                                    </div>
                                    <span className="text-xs font-mono text-text-secondary">{result.testId}</span>
                                </div>

                                <div className="p-5">
                                    <h4 className="font-bold text-text-primary mb-1 truncate">{spec?.testName}</h4>
                                    <div className="flex items-center gap-2 text-xs text-text-secondary mb-4">
                                        <span className="flex items-center gap-1"><Calendar size={12} /> {result.testDate}</span>
                                        <span className="flex items-center gap-1"><BookOpen size={12} /> {result.sampleId}</span>
                                    </div>

                                    {result.failureMode && (
                                        <div className="mb-4 bg-red-50 dark:bg-red-900/10 p-3 rounded-lg border border-red-100 dark:border-red-900/30">
                                            <div className="text-xs font-bold text-red-800 dark:text-red-400 uppercase mb-1 flex items-center gap-1">
                                                <AlertTriangle size={12} /> Failure Mode
                                            </div>
                                            <p className="text-sm text-red-700 dark:text-red-300 line-clamp-2">{result.failureMode}</p>
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        {result.measurements.slice(0, 3).map((m, i) => {
                                            const paramName = m.parameterId;  // testParameters not available in DvprTest
                                            const unit = '';  // testParameters not available in DvprTest
                                            return (
                                                <div key={i} className="flex justify-between text-sm items-center border-b border-border/50 pb-1 last:border-0">
                                                    <span className="text-text-secondary truncate max-w-[60%]">{paramName}</span>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-mono font-medium">{m.measuredValue} <span className="text-text-secondary text-xs">{unit}</span></span>
                                                        <span className={`w-2 h-2 rounded-full ${m.passFail === 'Pass' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                                <div className="px-5 py-3 bg-slate-50 dark:bg-black/20 border-t border-border flex justify-between items-center">
                                    <span className="text-xs text-text-secondary">Tested by: {result.testedBy}</span>
                                    <button
                                        onClick={() => console.log(`🔍 Viewing Granular Test Details for ${spec?.testName}`)}
                                        className="text-xs font-medium text-primary hover:underline flex items-center gap-1"
                                    >
                                        View Details <ChevronRight size={12} />
                                    </button>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        );
    };

    // --- COMPONENT: TEST RESULTS TIMELINE ---

    const TestResultsTimeline = () => {
        const tests = projectTests.filter(t =>
            localTestResults.some(r => r.testId === t.testId)
        );

        return (
            <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden h-full flex flex-col">
                <div className="p-6 border-b border-border flex justify-between items-center">
                    <div>
                        <h2 className="text-xl font-bold text-text-primary">Reliability Growth Matrix</h2>
                        <p className="text-text-secondary text-sm">Test performance evolution across iterations</p>
                    </div>
                    <div className="flex gap-2">
                        <div className="px-3 py-1 rounded bg-green-100 text-green-800 text-xs font-bold">PASS</div>
                        <div className="px-3 py-1 rounded bg-red-100 text-red-800 text-xs font-bold">FAIL</div>
                        <div className="px-3 py-1 rounded bg-slate-100 text-slate-800 text-xs font-bold">NO TEST</div>
                    </div>
                </div>
                <div className="overflow-auto flex-1 p-6">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr>
                                <th className="text-left p-3 min-w-[200px] sticky left-0 bg-card z-10 font-bold text-text-secondary border-b border-border">Test Name</th>
                                {projectIterations.length > 0 ? (
                                    projectIterations.reverse().map(iter => {
                                        const iterNum = iter.epochNumber || iter.iteration || 0;
                                        return (
                                            <th key={iterNum} className="p-2 text-center text-xs font-mono text-text-secondary border-b border-border min-w-[40px]">
                                                I{iterNum}
                                            </th>
                                        );
                                    })
                                ) : (
                                    <th className="p-2 text-center text-xs font-mono text-text-secondary border-b border-border min-w-[40px]">
                                        I1
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {tests.map(test => (
                                <tr key={test.testId} className="hover:bg-slate-50 dark:hover:bg-white/5 border-b border-border/50">
                                    <td className="p-3 text-sm font-medium text-text-primary sticky left-0 bg-card z-10 border-r border-border truncate max-w-[200px]" title={test.testName}>
                                        {test.testName}
                                    </td>
                                    {projectIterations.length > 0 ? (
                                        projectIterations.reverse().map(iteration => {
                                            const iterNum = iteration.epochNumber || iteration.iteration || 0;
                                            const result = localTestResults.find(r => r.testId === test.testId && r.iterationNumber === iterNum);
                                            return (
                                                <td key={iterNum} className="p-1 text-center">
                                                    {result ? (
                                                        <div className={`w-6 h-6 mx-auto rounded-full flex items-center justify-center text-[10px] font-bold cursor-pointer transition-transform hover:scale-125 ${result.overallResult === 'Pass'
                                                            ? 'bg-green-500 text-white shadow-sm shadow-green-200 dark:shadow-none'
                                                            : 'bg-red-500 text-white shadow-sm shadow-red-200 dark:shadow-none'
                                                            }`} title={`${iteration.epochName || `Iteration ${iterNum}`}: ${result.overallResult}`}>
                                                            {result.overallResult === 'Pass' ? 'P' : 'F'}
                                                        </div>
                                                    ) : (
                                                        <div className="w-2 h-2 mx-auto rounded-full bg-slate-200 dark:bg-slate-800"></div>
                                                    )}
                                                </td>
                                            );
                                        })
                                    ) : (
                                        <td className="p-1 text-center">
                                            <div className="w-2 h-2 mx-auto rounded-full bg-slate-200 dark:bg-slate-800"></div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // --- COMPONENT: TEST ENTRY FORM ---

    const TestEntryForm = () => {
        const [formData, setFormData] = useState({
            testId: '',
            iterationNumber: 18,
            testedBy: 'Admin',
            sampleId: `S-I18-${Math.floor(Math.random() * 100)}`
        });

        const selectedSpec = projectTests.find(t => t.testId === formData.testId);
        const params: any[] = [];  // testParameters not available in DvprTest

        const handleSubmit = (e: React.FormEvent) => {
            e.preventDefault();
            alert("Test result saved to LIMS database successfully!");
            // In a real app, this would append to localTestResults and save via API
        };

        return (
            <div className="max-w-4xl mx-auto">
                <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-border bg-slate-50 dark:bg-slate-800/50">
                        <h2 className="text-xl font-bold text-text-primary flex items-center gap-2">
                            <Beaker className="text-primary" /> LIMS Data Entry
                        </h2>
                        <p className="text-text-secondary text-sm mt-1">Record new verification test results</p>
                    </div>

                    <form onSubmit={handleSubmit} className="p-8 space-y-8">
                        {/* Meta Data Section */}
                        <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-text-primary">Select Test Procedure</label>
                                <select
                                    className="w-full p-3 bg-white dark:bg-black/20 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 outline-none transition-all"
                                    value={formData.testId}
                                    onChange={(e) => setFormData({ ...formData, testId: e.target.value })}
                                    required
                                >
                                    <option value="">-- Select Test --</option>
                                    {projectTests.map(t => (
                                        <option key={t.testId} value={t.testId}>{t.testId} - {t.testName}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-text-primary">Iteration</label>
                                    <input
                                        type="number"
                                        value={formData.iterationNumber}
                                        onChange={(e) => setFormData({ ...formData, iterationNumber: parseInt(e.target.value) })}
                                        className="w-full p-3 bg-white dark:bg-black/20 border border-border rounded-lg font-mono focus:ring-2 focus:ring-primary/50 outline-none"
                                    />
                                </div>
                                <div className="space-y-2 col-span-2">
                                    <label className="text-sm font-semibold text-text-primary">Sample ID</label>
                                    <input
                                        type="text"
                                        value={formData.sampleId}
                                        onChange={(e) => setFormData({ ...formData, sampleId: e.target.value })}
                                        className="w-full p-3 bg-white dark:bg-black/20 border border-border rounded-lg font-mono focus:ring-2 focus:ring-primary/50 outline-none"
                                    />
                                </div>
                            </div>
                        </div>

                        {selectedSpec && (
                            <div className="animate-in fade-in slide-in-from-top-4 duration-500">
                                <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-lg p-4 mb-6 flex items-start gap-3">
                                    <BookOpen className="text-blue-600 mt-1" size={18} />
                                    <div>
                                        <h4 className="font-bold text-blue-800 dark:text-blue-300">Procedure Summary</h4>
                                        <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">{selectedSpec.testMethod}</p>
                                    </div>
                                </div>

                                <h3 className="text-lg font-bold border-b border-border pb-2 mb-4">Measurements</h3>
                                <div className="space-y-4">
                                    {params.map(param => (
                                        <div key={param.parameterId} className="grid grid-cols-12 gap-4 items-center bg-slate-50 dark:bg-white/5 p-4 rounded-lg border border-border">
                                            <div className="col-span-4">
                                                <div className="font-medium text-text-primary">{param.parameterName}</div>
                                                <div className="text-xs text-text-secondary">Range: {param.lowerLimit} - {param.upperLimit} {param.unit}</div>
                                            </div>
                                            <div className="col-span-4">
                                                <div className="relative">
                                                    <input
                                                        type="number"
                                                        placeholder="Enter value"
                                                        className="w-full p-2 pr-12 border border-border rounded focus:ring-2 focus:ring-primary/50 outline-none font-mono"
                                                    />
                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary text-sm font-mono">{param.unit}</span>
                                                </div>
                                            </div>
                                            <div className="col-span-4 flex items-center gap-2">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input type="radio" name={`pf-${param.parameterId}`} className="w-4 h-4 text-green-600 focus:ring-green-500" defaultChecked />
                                                    <span className="text-sm font-medium">Pass</span>
                                                </label>
                                                <label className="flex items-center gap-2 cursor-pointer ml-4">
                                                    <input type="radio" name={`pf-${param.parameterId}`} className="w-4 h-4 text-red-600 focus:ring-red-500" />
                                                    <span className="text-sm font-medium">Fail</span>
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-8 pt-6 border-t border-border flex justify-end gap-4">
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('dashboard')}
                                        className="px-6 py-2 border border-border rounded-lg font-medium hover:bg-slate-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all flex items-center gap-2 shadow-lg shadow-primary/20">
                                        <Save size={18} /> Save Results
                                    </button>
                                </div>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        )
    }

    // --- COMPONENT: ANALYTICS DASHBOARD ---

    const AnalyticsDashboard = () => {
        // Calculate dynamic summary from actual project data
        const summary = useMemo(() => {
            // Calculate results by iteration using projectIterations
            const resultsByIteration = projectIterations.map(iter => {
                const iterNum = iter.epochNumber || iter.iteration || 0;
                const iterResults = localTestResults.filter(r => r.iterationNumber === iterNum);
                return {
                    iteration: iterNum,
                    iterationName: iter.epochName || `Iteration ${iterNum}`,
                    testsRun: iterResults.length,
                    passed: iterResults.filter(r => r.overallResult === 'Pass').length,
                    failed: iterResults.filter(r => r.overallResult === 'Fail').length
                };
            });

            // Calculate test distribution by type
            const testsByType: Record<string, number> = {};
            projectTests.forEach(test => {
                testsByType[test.testType] = (testsByType[test.testType] || 0) + 1;
            });

            return {
                totalTests: projectTests.length,
                resultsByIteration,
                testsByType
            };
        }, [localTestResults, projectTests]);

        return (
            <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2 bg-card border border-border rounded-xl p-6 shadow-sm">
                    <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
                        <TrendingUp className="text-primary" /> Reliability Growth Curve
                    </h3>
                    <div className="h-64 flex items-end justify-between gap-2 px-4 border-b border-l border-border relative">
                        {/* Mock Chart Bars */}
                        {summary.resultsByIteration.map((d, i) => {
                            const passRate = d.testsRun > 0 ? (d.passed / d.testsRun) * 100 : 0;
                            return (
                                <div key={i} className="flex-1 flex flex-col justify-end items-center group relative cursor-pointer">
                                    <div
                                        className={`w-full max-w-[30px] rounded-t transition-all hover:brightness-110 ${passRate === 100 ? 'bg-green-500' :
                                            passRate > 80 ? 'bg-emerald-400' :
                                                passRate > 50 ? 'bg-yellow-400' : 'bg-red-400'
                                            }`}
                                        style={{ height: `${Math.max(passRate, 5)}%` }}
                                    >
                                        <div className="opacity-0 group-hover:opacity-100 absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs py-1 px-2 rounded whitespace-nowrap z-10 pointer-events-none">
                                            Iteration {d.iteration}: {Math.round(passRate)}%
                                        </div>
                                    </div>
                                    <div className="mt-2 text-[10px] text-text-secondary font-mono">I{d.iteration}</div>
                                </div>
                            )
                        })}
                    </div>
                </div>

                <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
                    <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
                        <PieChart className="text-purple-500" /> Test Distribution
                    </h3>
                    <div className="space-y-4">
                        {Object.entries(summary.testsByType).map(([type, count]) => {
                            const percent = summary.totalTests > 0 ? ((count as number) / summary.totalTests) * 100 : 0;
                            return (
                                <div key={type} className="space-y-1">
                                    <div className="flex justify-between text-sm">
                                        <span className="font-medium text-text-primary">{type}</span>
                                        <span className="text-text-secondary">{count} Tests ({Math.round(percent)}%)</span>
                                    </div>
                                    <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                                        <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${percent}%` }}></div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>

                <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
                    <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
                        <AlertCircle className="text-orange-500" /> Key Failures
                    </h3>
                    <div className="space-y-3">
                        {localTestResults.filter(r => r.overallResult === 'Fail').slice(0, 5).map(r => (
                            <div key={r.resultId} className="flex items-start gap-3 text-sm p-3 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-100 dark:border-red-900/30">
                                <span className="font-mono text-xs text-red-600 bg-white dark:bg-black/20 border border-red-200 px-1 rounded shrink-0 w-8 text-center">{r.iterationNumber}</span>
                                <div>
                                    <div className="font-bold text-red-800 dark:text-red-400 mb-0.5">{projectTests.find(t => t.testId === r.testId)?.testName}</div>
                                    <div className="text-red-700 dark:text-red-300 text-xs line-clamp-1">{r.failureMode}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // --- MAIN RENDER ---

    return (
        <div className="flex flex-col gap-6 h-full p-6 max-w-[1600px] mx-auto animate-in fade-in duration-500">

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
                        <TestTube2 size={32} className="text-primary" />
                        LIMS & Verification Suite
                    </h1>
                    <p className="text-text-secondary mt-1 ml-11">
                        Laboratory Information Management System - DVP&R Tracking - Reliability Analysis
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <ActiveProjectBadge showStandard={false} />
                    <ModuleInfoModal
                        title="LIMS & Verification Suite"
                        description="This module connects your requirements and risks (FMEA) to physical validation. It serves as both a Laboratory Information Management System (LIMS) and a DVP&R (Design Verification Plan & Report) hub."
                        description2="Track tests across 17 iterations, view reliability growth curves, and ensure every high-risk failure mode has a corresponding validation test."
                        benefits={[
                            "Full traceability: Link FMEA Risks -> Test Specs -> Lab Results.",
                            "Visualize Reliability Growth: See how pass rates improve from prototype to production.",
                            "Automated DVP&R: Matrices are auto-populated based on test results.",
                            "Digital Lab Notebook: Enter and track granular measurement data."
                        ]}
                        usageInstructions={[
                            "Use 'Test Specifications' to browse standard procedures and pass/fail criteria.",
                            "Check 'Risk Traceability' to ensure your top failure modes are being tested.",
                            "Switch to 'Iteration Dashboard' to see the pass/fail status of the current build.",
                            "Use 'Lab Entry' to record new test data points."
                        ]}
                    />
                    <button
                        onClick={async () => {
                            const url = await exportProject('pdf');
                            if (url) window.open(url, '_blank');
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-card border border-border rounded-lg text-sm font-medium shadow-sm hover:bg-slate-50 transition-colors"
                    >
                        <FileText size={16} /> Export Report
                    </button>
                    <button
                        onClick={() => setActiveTab('entry')}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium shadow-md shadow-primary/20 hover:bg-primary/90 transition-colors"
                    >
                        <Plus size={16} /> New Test Entry
                    </button>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex bg-card border border-border rounded-xl p-1 shadow-sm overflow-x-auto">
                {[
                    { id: 'specs', label: 'Test Specifications', icon: <BookOpen size={18} /> },
                    { id: 'traceability', label: 'Risk Traceability (DVP&R)', icon: <Link2 size={18} /> },
                    { id: 'results', label: 'Results Timeline', icon: <Calendar size={18} /> },
                    { id: 'dashboard', label: 'Iteration Dashboard', icon: <Activity size={18} /> },
                    { id: 'entry', label: 'Lab Entry', icon: <Beaker size={18} /> },
                    { id: 'analytics', label: 'Analytics', icon: <BarChart3 size={18} /> },
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as TabType)}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === tab.id
                            ? 'bg-primary text-white shadow-md'
                            : 'text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-800'
                            }`}
                    >
                        {tab.icon}
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content Area */}
            <div className="flex-1 min-h-[500px]">
                {activeTab === 'specs' && <TestSpecificationViewer />}
                {activeTab === 'traceability' && <TraceabilityMatrix />}
                {activeTab === 'results' && <TestResultsTimeline />}
                {activeTab === 'dashboard' && <IterationDashboard />}
                {activeTab === 'entry' && <TestEntryForm />}
                {activeTab === 'analytics' && <AnalyticsDashboard />}
            </div>
        </div>
    );
};

export default ReliabilityTestPlan;
